import React, { useState, useEffect, useRef } from "react";
import { Container } from "reactstrap";
import {
  Button,
  Grid,
  TextField,
  Typography,
  InputAdornment,
  Modal,
  IconButton,
  Box,
  Paper,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import axios from "axios";
import { Controller, useForm } from "react-hook-form";
import { useAlert } from "react-alert";
import { useNavigate } from "react-router-dom";
import Moment from "moment";
import Swal from "sweetalert2";
import { m } from "framer-motion";

const LetterForm = (props) => {
  const { FormId, FormPatientId, onFormSubmit, handleClose } = props;
  const alert = useAlert();
  const navigate = useNavigate();

  const [submitting, setSubmitting] = useState(false);
  const [datee, setDatee] = useState("");
  const [patientData, setPatientData] = useState({
    planName: "",
    city: "",
    state: "",
    zipCode: "",
    address: "",
    phoneNumber: "",
  });
  const [totalScore, setTotalScore] = useState(0);

  const fields = [
    "id",
    "patientId",
    "practiceId",
    "patientName",
    "insuranceName",
    "address",
    "city",
    "state",
    "zip",
    "phone",
    "heartProblems",
    "highBloodPressure",
    "diabetes",
    "thyroidSyndrome",
    "takesPrescribedMedicines",
    "medicineList",
    "alcoholUnitsPerWeek",
    "smokingPerDay",
    "sleepinessInterferesDaily",
    "impairedCognition",
    "insomnia",
    "ischemicHeartDisease",
    "inappropriateDaytimeNapping",
    "excessiveDaytimeSleepiness",
    "moodDisorders",
    "hypertension",
    "historyOfStroke",
    "triedCPAP",
    "usingCPAP",
    "cpapDaysPerWeek",
    "cpapCausedSinusIssues",
    "cpapClaustrophobia",
    "cpapgI_Issues",
    "cpapLatexAllergy",
    "cpapOtherReason",
    "previousSleepStudy",
    "ahI_Score",
    "triedOralSleepAppliance",
    "oralApplianceYear",
    "otherTherapiesComments",
    "esS_SittingReading",
    "esS_WatchingTV",
    "esS_PublicPlace",
    "esS_PassengerInCar",
    "esS_LayingToRest",
    "esS_TalkingToSomeone",
    "esS_AfterLunchNoAlcohol",
    "esS_TrafficStop",
    "esS_Total",
    "patientSignature",
    "signatureDate",
    "createdDate",
    "createdBy",
    "updatedDate",
    "updatedBy",
    "inactive",
  ];
  const defValues = {
    id: FormId,
    patientId: 0,
    practiceId: 0,
    patientName: "",
    insuranceName: "",
    address: "",
    city: "",
    state: "",
    zip: "",
    phone: "",
    heartProblems: null,
    highBloodPressure: null,
    diabetes: null,
    thyroidSyndrome: null,
    takesPrescribedMedicines: null,
    medicineList: "",
    alcoholUnitsPerWeek: "",
    smokingPerDay: "",
    sleepinessInterferesDaily: null,
    impairedCognition: null,
    insomnia: null,
    ischemicHeartDisease: null,
    inappropriateDaytimeNapping: null,
    excessiveDaytimeSleepiness: null,
    moodDisorders: null,
    hypertension: null,
    historyOfStroke: null,
    triedCPAP: null,
    usingCPAP: null,
    cpapDaysPerWeek: "",
    cpapCausedSinusIssues: null,
    cpapClaustrophobia: null,
    cpapgI_Issues: null,
    cpapLatexAllergy: null,
    cpapOtherReason: "",
    previousSleepStudy: null,
    ahI_Score: "",
    triedOralSleepAppliance: null,
    oralApplianceYear: "",
    otherTherapiesComments: "",
    esS_SittingReading: "",
    esS_WatchingTV: "",
    esS_PublicPlace: "",
    esS_PassengerInCar: "",
    esS_LayingToRest: "",
    esS_TalkingToSomeone: "",
    esS_AfterLunchNoAlcohol: "",
    esS_TrafficStop: "",
    esS_Total: "",
    patientSignature: "",
    signatureDate: "",
    createdDate: "",
    createdBy: "",
    updatedDate: "",
    updatedBy: "",
    inactive: false,
  };

  const {
    register,
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: defValues,
    mode: "onBlur",
  });

  const token = localStorage.getItem("Token");
  const auth = `Bearer ${token}`;
  const headers = { Authorization: auth };

  // Watch the epworth scale fields to calculate total
  const watchEpworthFields = watch([
    "esS_SittingReading",
    "esS_WatchingTV",
    "esS_PublicPlace",
    "esS_PassengerInCar",
    "esS_LayingToRest",
    "esS_TalkingToSomeone",
    "esS_AfterLunchNoAlcohol",
    "esS_TrafficStop",
  ]);

  // Calculate total score when epworth fields change
  useEffect(() => {
    const total = watchEpworthFields.reduce(
      (sum, value) => sum + (parseInt(value) || 0),
      0,
    );
    setTotalScore(total);
    setValue("esS_Total", total);
  }, [watchEpworthFields, setValue]);

  // Load form data if FormId exists (edit mode)
  useEffect(() => {
    if (FormId) {
      axios
        .get(
          `${process.env.REACT_APP_API_URL}/PatientForms/GetFormScreenerId?Id=${FormId}`,
          { headers },
        )
        .then((response) => {
          fields.forEach((field) => {
            if (response[field] !== undefined) {
              setValue(field, response[field]);
            }
          });

          if (response.signatureDate) {
            setDatee(Moment(response.signatureDate).format("YYYY-MM-DD"));
          }
        })
        .catch((error) => {
          console.error("Error loading form data:", error);
        });
    }
  }, [FormId]);

  // Load patient insurance info
  useEffect(() => {
    if (FormPatientId) {
      axios
        .get(
          `${process.env.REACT_APP_API_URL}/Patients/SearchInsuranceName?patientId=${FormPatientId}`,
          { headers },
        )
        .then((response) => {
          if (response && response[0]) {
            const patientInfo = response[0];
            setPatientData({
              planName: patientInfo.planName || "",
              city: patientInfo.city || "",
              state: patientInfo.state || "",
              zipCode: patientInfo.zipCode || "",
              address: patientInfo.address || "",
              phoneNumber: patientInfo.phoneNumber || "",
            });

            // Auto-fill form with patient data
            setValue("insuranceName", patientInfo.planName || "");
            setValue("patientName", patientInfo.patientName || "");
            setValue("city", patientInfo.city || "");
            setValue("state", patientInfo.state || "");
            setValue("zip", patientInfo.zipCode || "");
            setValue("address", patientInfo.address || "");
            setValue("phone", patientInfo.phoneNumber || "");
          }
        })
        .catch((error) => {
          console.error("Error loading patient data:", error);
        });
    }
  }, [FormPatientId, setValue]);

  const onSubmit = (data) => {
    setSubmitting(true);
    const postData = {
      ...data,
      esS_Total: totalScore,
      signatureDate: datee || new Date().toISOString(),
      patientId: FormPatientId,
    };

    axios
      .post(
        `${process.env.REACT_APP_API_URL}/PatientForms/AddFormScreener`,
        postData,
        { headers },
      )
      .then((response) => {
        Swal.fire({
          icon: "success",
          title: "Form Added!",
          html: `<p>(OSA) Screener Form has been saved successfully.</p>`,
          timer: 2000, // 2 seconds
          showConfirmButton: false,
          timerProgressBar: true,
          willOpen: () => {
            const swalPopup = document.querySelector(".swal2-container");
            if (swalPopup) swalPopup.style.zIndex = 2200;
          },
        });
        if (onFormSubmit) {
          onFormSubmit(response.id);
        }

        handleClose();
      })
      .catch((error) => {
        setSubmitting(false);
        Swal.fire({
          icon: "error",
          title: "Failed!",
          html: `<p>${
            error.response?.data?.message ||
            "Something went wrong. Please try again."
          }</p>`,
          confirmButtonColor: "#F44336",
          willOpen: () => {
            const swalPopup = document.querySelector(".swal2-container");
            if (swalPopup) swalPopup.style.zIndex = 2200;
          },
        });
      });
  };
  const [progressNoteDialogOpen, setProgressNoteDialogOpen] = useState(false);
  const [progressNoteContent, setProgressNoteContent] = useState("");
  const formRef = useRef(null);
  const [editorHTML, setEditorHTML] = useState("");
  const replaceInputsWithText = (container) => {
    if (!container) return;

    // Replace radios with ☑/☐
    const radios = container.querySelectorAll('input[type="radio"]');
    radios.forEach((radio) => {
      const checked = radio.checked ? "☑" : "☐";
      const span = document.createElement("span");
      span.innerHTML = `${checked}`;
      span.style.marginRight = "12px";
      span.style.display = "inline-block";
      span.style.lineHeight = "1.5"; // gives extra height
      radio.parentNode.replaceChild(span, radio);
    });

    // Replace checkboxes with ☑/☐
    const checkboxes = container.querySelectorAll('input[type="checkbox"]');
    checkboxes.forEach((checkbox) => {
      const checked = checkbox.checked ? "☑" : "☐";
      const span = document.createElement("span");
      span.innerHTML = `${checked}`;
      span.style.marginRight = "12px";
      span.style.display = "inline-block";
      span.style.lineHeight = "1.5"; // gives extra height
      checkbox.parentNode.replaceChild(span, checkbox);
    });

    // Replace all text/date/number inputs with bold text
    const textInputs = container.querySelectorAll(
      'input:not([type="radio"]):not([type="checkbox"])',
    );
    textInputs.forEach((input) => {
      const value = input.value || "-";
      const span = document.createElement("span");
      span.innerText = value;
      span.style.fontWeight = "bold";
      span.style.display = "inline-block";
      span.style.padding = "6px 8px"; // adds height/padding
      span.style.minWidth = "120px"; // optional: ensures spacing
      span.style.backgroundColor = "#f0f0f0"; // optional subtle background
      span.style.borderRadius = "4px";
      input.parentNode.replaceChild(span, input);
    });
  };
  const progressNoteHeader = `
  <div
    style="
      display: flex;
      align-items: center;
      justify-content: center;
      height: 64px;
      padding: 0 18px;
      background: #3C7F6B;
      box-shadow: 0 3px 10px rgba(0,0,0,0.18);
      margin-bottom: 16px;
    "
  >
    <h1
      style="
        margin: 0;
        font-weight: 600;
        color: #ffffff;
        font-size: 1.35rem;
        letter-spacing: 0.6px;
        text-align: center;
        font-family: Calibri, Segoe UI, sans-serif;
      "
    >
      Snoring and Obstructive Sleep Apnea (OSA) Screener
    </h1>
  </div>
`;

  const convertToProgressNote = (formData) => {
    if (!formRef.current) return;
    const formClone = formRef.current.cloneNode(true);
    replaceInputsWithText(formClone);
    const html = `
  <div
    style="
      font-family: Calibri, Segoe UI, sans-serif;
      color: #000;
      padding: 20px;
      max-width: 900px;

      background: #ffffff;
      border: 1.5px solid #cfd8dc;
      border-radius: 10px;

      box-shadow: 0 4px 14px rgba(0,0,0,0.08);
    "
  >
    ${progressNoteHeader}

    <div style="padding:8px;">
      ${formClone.innerHTML}
    </div>
  </div>
`;
    setEditorHTML(html);
    setProgressNoteDialogOpen(true);
  };

  const onSubmitProgresNote = (data) => {
    const today = new Date().toISOString().split("T")[0];
    const postData = {
      patientId: FormPatientId,
      patientNoteId: 0,
      noteText: editorHTML,
      signedDate: "",
      visitDate: today,
    };

    axios
      .post(
        `${process.env.REACT_APP_API_URL}/PatientNote/AddPatientNote`,
        postData,
        { headers },
      )
      .then((response) => {
        setProgressNoteDialogOpen(false);
        handleClose();
        Swal.fire({
          icon: "success",
          timer: 1000,
          showConfirmButton: false,
          title: "Note saved successfully.",
          willOpen: () => {
            const container = document.querySelector(".swal2-container");
            if (container) container.style.zIndex = "1800";
          },
        });
      })
      .catch((error) => {
        Swal.fire({
          icon: "error",
          title: `Error ${error.message}`,
          timer: 1000,
          willOpen: () => {
            const container = document.querySelector(".swal2-container");
            if (container) container.style.zIndex = "1800";
          },
        });
      });
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4, m: 2 }}>
      <Modal
        open={progressNoteDialogOpen}
        onClose={() => setProgressNoteDialogOpen(false)}
        aria-labelledby="progress-note-modal"
        sx={{
          zIndex: 1820,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 2,
        }}
      >
        <Paper
          elevation={24}
          sx={{
            width: "95%",
            maxWidth: 1000,
            maxHeight: "95vh",
            display: "flex",
            flexDirection: "column",
            borderRadius: 3,
            overflow: "hidden",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              background: "linear-gradient(135deg, #2c5aa0 0%, #1976d2 100%)",
              color: "white",
              p: 1.5,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box>
              <Typography
                variant="h5"
                fontWeight="bold"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  color: "#fff",
                }}
              >
                Progress Note
              </Typography>
            </Box>
            <IconButton
              onClick={() => setProgressNoteDialogOpen(false)}
              sx={{ color: "white" }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Toolbar */}
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1,
              p: 1,
              backgroundColor: "#f9f9f9",
              borderBottom: "1px solid #ddd",
            }}
          >
            {[
              { label: "B", command: "bold", style: { fontWeight: "bold" } },
              { label: "I", command: "italic", style: { fontStyle: "italic" } },
              {
                label: "U",
                command: "underline",
                style: { textDecoration: "underline" },
              },
              {
                label: "S",
                command: "strikeThrough",
                style: { textDecoration: "line-through" },
              },
              { label: "Sub", command: "subscript" },
              { label: "Sup", command: "superscript" },
              { label: "• List", command: "insertUnorderedList" },
              { label: "1. List", command: "insertOrderedList" },
              { label: "Left", command: "justifyLeft" },
              { label: "Center", command: "justifyCenter" },
              { label: "Right", command: "justifyRight" },
              { label: "Justify", command: "justifyFull" },
              { label: "H1", command: "formatBlock", value: "h1" },
              { label: "H2", command: "formatBlock", value: "h2" },
              { label: "Quote", command: "formatBlock", value: "blockquote" },
              { label: "Code", command: "formatBlock", value: "pre" },
              { label: "Link", command: "createLink", prompt: true },
              { label: "Text Color", command: "foreColor", color: "#000000" },
              { label: "BG Color", command: "hiliteColor", color: "#FFFF00" },
            ].map((btn) => (
              <Button
                key={btn.label}
                size="small"
                onMouseDown={(e) => {
                  e.preventDefault(); // prevent losing focus
                  if (btn.prompt) {
                    const url = prompt("Enter URL");
                    if (url) document.execCommand(btn.command, false, url);
                  } else if (btn.color) {
                    document.execCommand(btn.command, false, btn.color);
                  } else if (btn.value) {
                    document.execCommand(btn.command, false, btn.value);
                  } else {
                    document.execCommand(btn.command, false, null);
                  }
                }}
                sx={{
                  minWidth: 35,
                  fontWeight: "bold",
                  textTransform: "none",
                  ...btn.style,
                  border: "1px solid #ddd",
                  borderRadius: 2,
                  backgroundColor: "#fff",
                  "&:hover": {
                    backgroundColor: "#e0e0e0",
                  },
                }}
              >
                {btn.label}
              </Button>
            ))}
          </Box>

          {/* Editable content */}
          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              backgroundColor: "#fdfdfd",
            }}
          >
            <div
              contentEditable
              suppressContentEditableWarning
              style={{
                minHeight: "75vh",
                border: "1px solid #ccc",
                borderRadius: 4,
                padding: "16px",
                fontFamily: "'Calibri', 'Segoe UI', sans-serif",
                fontSize: 14,
                backgroundColor: "#fff",
                overflowY: "auto",
              }}
              dangerouslySetInnerHTML={{ __html: editorHTML }}
            />
          </Box>

          <Grid
            container
            spacing={1}
            justifyContent="flex-end"
            sx={{
              bgcolor: "#f5f5f5",
              p: 1.5,
              borderTop: 1,
              borderColor: "divider",
              display: "flex",
            }}
          >
            <Grid item>
              <Button
                type="submit"
                variant="contained"
                onClick={onSubmitProgresNote}
                sx={{
                  minWidth: 200,
                  height: 40,
                  borderColor: "#4CAF50",
                  backgroundColor: "#4CAF50",
                  color: "#fff",
                  "&:hover": {
                    backgroundColor: "#4CAF50",
                    color: "white",
                    borderColor: "#4CAF50",
                  },
                }}
              >
                Save Progress Note
              </Button>
            </Grid>
            <Grid item>
              <Button
                variant="contained"
                color="primary"
                onClick={() => setProgressNoteDialogOpen(false)}
                disabled={submitting}
              >
                Close
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </Modal>
      {/* Header Section */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12}>
          <Typography
            variant="h4"
            align="center"
            fontWeight="bold"
            color="primary"
            gutterBottom
          >
            Snoring and Obstructive Sleep Apnea (OSA) Screener
          </Typography>
        </Grid>
      </Grid>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container ref={formRef} spacing={1} sx={{ padding: 2 }}>
          <Grid item xs={12}>
            {/* Patient Information Section */}
            <Typography variant="body1" align="center" sx={{ mb: 3 }}>
              Snoring and daytime sleepiness can have a profound impact on
              quality of life:
            </Typography>

            <Grid container spacing={1} sx={{ mb: 3 }}>
              {[
                {
                  title: "• Daytime sleepiness",
                  desc: "- less effectiveness at work and increased risk of accidents",
                },
                {
                  title: "• Reduced energy-poor",
                  desc: "motivation to exercise and weight gain",
                },
                {
                  title: "• Relationship issues",
                  desc: "- sleeping in different bedrooms, reduced sex life, and higher stress levels",
                },
                {
                  title: "• Hypertension",
                  desc: "- those who snore or suffer from OSA have an elevated risk of high blood pressure",
                },
              ].map((item, idx) => (
                <Grid item xs={12} key={idx}>
                  <Typography variant="body2">
                    <strong>{item.title}</strong> {item.desc}
                  </Typography>
                </Grid>
              ))}
            </Grid>

            <Typography
              variant="body2"
              sx={{ fontStyle: "italic", textAlign: "center", mb: 4 }}
            >
              OSA is a serious condition in which a person stops breathing (or
              suffers extremely low oxygen levels) while asleep. It often occurs
              in conjunction with snoring.
            </Typography>
          </Grid>
          <Grid
            container
            spacing={3}
            sx={{ m: 2, p: 2, backgroundColor: "#f8f9fa", borderRadius: 2 }}
          >
            <Grid item xs={12}>
              <Typography
                variant="h6"
                fontWeight="bold"
                color="primary"
                gutterBottom
              >
                Patient Information
              </Typography>
            </Grid>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Controller
                  name="patientName"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Patient Name"
                      fullWidth
                      variant="outlined"
                      size="small"
                      InputProps={{ readOnly: true }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Controller
                  name="insuranceName"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Insurance"
                      fullWidth
                      variant="outlined"
                      size="small"
                      InputProps={{ readOnly: true }}
                      value={patientData.planName}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Controller
                  name="address"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Address"
                      fullWidth
                      variant="outlined"
                      size="small"
                      InputProps={{ readOnly: true }}
                      value={patientData.address}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Controller
                  name="phone"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Phone #"
                      fullWidth
                      variant="outlined"
                      size="small"
                      InputProps={{ readOnly: true }}
                      value={patientData.phoneNumber}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Controller
                  name="city"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="City"
                      fullWidth
                      variant="outlined"
                      size="small"
                      InputProps={{ readOnly: true }}
                      value={patientData.city}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Controller
                  name="state"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="State"
                      fullWidth
                      variant="outlined"
                      size="small"
                      InputProps={{ readOnly: true }}
                      value={patientData.state}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Controller
                  name="zip"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Zip Code"
                      fullWidth
                      variant="outlined"
                      size="small"
                      InputProps={{ readOnly: true }}
                      value={patientData.zipCode}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Grid>

          {/* Lifestyle and General Health Section */}
          <Grid
            container
            spacing={3}
            sx={{ m: 2, p: 2, backgroundColor: "#f8f9fa", borderRadius: 2 }}
          >
            <Grid item xs={12}>
              <Typography
                variant="h6"
                fontWeight="bold"
                color="primary"
                gutterBottom
              >
                Lifestyle and General Health
              </Typography>
              <Typography variant="body2" sx={{ mb: 3 }}>
                The following questions relate to your lifestyle and general
                health. Please indicate whether you suffer from any of the
                below, providing further details when the answer is yes.
              </Typography>
            </Grid>

            {/* Heart Problems */}
            <Grid item xs={12}>
              <Controller
                name="heartProblems"
                control={control}
                render={({ field }) => (
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 12 }}
                  >
                    <Typography variant="body2" sx={{ minWidth: 180 }}>
                      Heart problems
                    </Typography>
                    <input
                      type="radio"
                      checked={field.value === true}
                      onClick={() => field.onChange(true)}
                    />
                    Yes
                    <input
                      type="radio"
                      checked={field.value === false}
                      onClick={() => field.onChange(false)}
                      style={{ marginLeft: 20 }}
                    />
                    No
                  </div>
                )}
              />
            </Grid>

            {/* High Blood Pressure */}
            <Grid item xs={12}>
              <Controller
                name="highBloodPressure"
                control={control}
                render={({ field }) => (
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 12 }}
                  >
                    <Typography variant="body2" sx={{ minWidth: 180 }}>
                      High blood pressure
                    </Typography>
                    <input
                      type="radio"
                      checked={field.value === true}
                      onClick={() => field.onChange(true)}
                    />
                    Yes
                    <input
                      type="radio"
                      checked={field.value === false}
                      onClick={() => field.onChange(false)}
                      style={{ marginLeft: 20 }}
                    />
                    No
                  </div>
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="diabetes"
                control={control}
                render={({ field }) => (
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 12 }}
                  >
                    <Typography variant="body2" sx={{ minWidth: 180 }}>
                      Diabetes
                    </Typography>
                    <input
                      type="radio"
                      checked={field.value === true}
                      onClick={() => field.onChange(true)}
                    />
                    Yes
                    <input
                      type="radio"
                      checked={field.value === false}
                      onClick={() => field.onChange(false)}
                      style={{ marginLeft: 20 }}
                    />
                    No
                  </div>
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="thyroidSyndrome"
                control={control}
                render={({ field }) => (
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 12 }}
                  >
                    <Typography variant="body2" sx={{ minWidth: 180 }}>
                      Thyroid syndrome
                    </Typography>
                    <input
                      type="radio"
                      checked={field.value === true}
                      onClick={() => field.onChange(true)}
                    />
                    Yes
                    <input
                      type="radio"
                      checked={field.value === false}
                      onClick={() => field.onChange(false)}
                      style={{ marginLeft: 20 }}
                    />
                    No
                  </div>
                )}
              />
            </Grid>
            {/* Thyroid Syndrome */}

            {/* Prescribed Medicines */}
            <Grid item xs={12} sx={{ mt: 3 }}>
              <Controller
                name="takesPrescribedMedicines"
                control={control}
                render={({ field }) => (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      marginBottom: 8,
                    }}
                  >
                    <Typography variant="body2" sx={{ minWidth: 180 }}>
                      Do you take any prescribed medicines?
                    </Typography>
                    <input
                      type="radio"
                      checked={field.value === true}
                      onClick={() => field.onChange(true)}
                    />
                    Yes
                    <input
                      type="radio"
                      checked={field.value === false}
                      onClick={() => field.onChange(false)}
                      style={{ marginLeft: 20 }}
                    />
                    No
                  </div>
                )}
              />
            </Grid>

            {/* Alcohol and Smoking */}
            <Grid item xs={12} md={6}>
              <Controller
                name="alcoholUnitsPerWeek"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Alcohol consumption"
                    type="number"
                    fullWidth
                    variant="outlined"
                    size="small"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          units/week
                        </InputAdornment>
                      ),
                    }}
                    onChange={(e) =>
                      field.onChange(parseInt(e.target.value) || 0)
                    }
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Controller
                name="smokingPerDay"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Smoking level"
                    type="number"
                    fullWidth
                    variant="outlined"
                    size="small"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">no/day</InputAdornment>
                      ),
                    }}
                    onChange={(e) =>
                      field.onChange(parseInt(e.target.value) || 0)
                    }
                  />
                )}
              />
            </Grid>
          </Grid>

          {/* Medical Conditions Section */}
          <Grid
            container
            spacing={3}
            sx={{ m: 2, p: 2, backgroundColor: "#f8f9fa", borderRadius: 2 }}
          >
            <Grid item xs={12}>
              <Typography
                variant="h6"
                fontWeight="bold"
                color="primary"
                gutterBottom
              >
                Please indicate if you have suffered from any of the conditions
                below:
              </Typography>
            </Grid>

            <Grid container spacing={2}>
              {/* Sleepiness that interferes with daily activities */}
              <Grid item xs={12} md={6}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="checkbox"
                    name="sleepinessInterferesDaily"
                    {...register("sleepinessInterferesDaily")}
                    style={{ cursor: "pointer" }}
                    onChange={(e) => {
                      setValue("sleepinessInterferesDaily", e.target.checked);
                    }}
                  />
                  <Typography variant="body2">
                    Sleepiness that interferes with daily activities
                  </Typography>
                </div>
              </Grid>

              {/* Excessive daytime sleepiness */}
              <Grid item xs={12} md={6}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="checkbox"
                    name="excessiveDaytimeSleepiness"
                    {...register("excessiveDaytimeSleepiness")}
                    style={{ cursor: "pointer" }}
                    onChange={(e) => {
                      setValue("excessiveDaytimeSleepiness", e.target.checked);
                    }}
                  />
                  <Typography variant="body2">
                    Excessive daytime sleepiness
                  </Typography>
                </div>
              </Grid>

              {/* Impaired cognition */}
              <Grid item xs={12} md={6}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="checkbox"
                    name="impairedCognition"
                    {...register("impairedCognition")}
                    style={{ cursor: "pointer" }}
                    onChange={(e) => {
                      setValue("impairedCognition", e.target.checked);
                    }}
                  />
                  <Typography variant="body2">Impaired cognition</Typography>
                </div>
              </Grid>

              {/* Mood disorders */}
              <Grid item xs={12} md={6}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="checkbox"
                    name="moodDisorders"
                    {...register("moodDisorders")}
                    style={{ cursor: "pointer" }}
                    onChange={(e) => {
                      setValue("moodDisorders", e.target.checked);
                    }}
                  />
                  <Typography variant="body2">Mood disorders</Typography>
                </div>
              </Grid>

              {/* Insomnia */}
              <Grid item xs={12} md={6}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="checkbox"
                    name="insomnia"
                    {...register("insomnia")}
                    style={{ cursor: "pointer" }}
                    onChange={(e) => {
                      setValue("insomnia", e.target.checked);
                    }}
                  />
                  <Typography variant="body2">Insomnia</Typography>
                </div>
              </Grid>

              {/* Hypertension */}
              <Grid item xs={12} md={6}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="checkbox"
                    name="hypertension"
                    {...register("hypertension")}
                    style={{ cursor: "pointer" }}
                    onChange={(e) => {
                      setValue("hypertension", e.target.checked);
                    }}
                  />
                  <Typography variant="body2">Hypertension</Typography>
                </div>
              </Grid>

              {/* Ischemic heart disease */}
              <Grid item xs={12} md={6}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="checkbox"
                    name="ischemicHeartDisease"
                    {...register("ischemicHeartDisease")}
                    style={{ cursor: "pointer" }}
                    onChange={(e) => {
                      setValue("ischemicHeartDisease", e.target.checked);
                    }}
                  />
                  <Typography variant="body2">
                    Ischemic heart disease
                  </Typography>
                </div>
              </Grid>

              {/* History of stroke */}
              <Grid item xs={12} md={6}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="checkbox"
                    name="historyOfStroke"
                    {...register("historyOfStroke")}
                    style={{ cursor: "pointer" }}
                    onChange={(e) => {
                      setValue("historyOfStroke", e.target.checked);
                    }}
                  />
                  <Typography variant="body2">History of stroke</Typography>
                </div>
              </Grid>

              {/* Inappropriate daytime napping */}
              <Grid item xs={12} md={6}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="checkbox"
                    name="inappropriateDaytimeNapping"
                    {...register("inappropriateDaytimeNapping")}
                    style={{ cursor: "pointer" }}
                    onChange={(e) => {
                      setValue("inappropriateDaytimeNapping", e.target.checked);
                    }}
                  />
                  <Typography variant="body2">
                    Inappropriate daytime napping (e.g., during driving,
                    conversation, or eating)
                  </Typography>
                </div>
              </Grid>
            </Grid>
          </Grid>

          {/* CPAP Information Section */}
          <Grid
            container
            spacing={3}
            sx={{ m: 2, p: 2, backgroundColor: "#f8f9fa", borderRadius: 2 }}
          >
            <Grid item xs={12}>
              <Typography
                variant="h6"
                fontWeight="bold"
                color="primary"
                gutterBottom
              >
                CPAP Information
              </Typography>
            </Grid>

            {/* Tried CPAP */}
            <Grid item xs={12}>
              <Controller
                name="triedCPAP"
                control={control}
                render={({ field }) => (
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 12 }}
                  >
                    <Typography variant="body2" sx={{ minWidth: 180 }}>
                      Have you tried a Nasal CPAP?
                    </Typography>
                    <input
                      type="radio"
                      checked={field.value === true}
                      onClick={() => field.onChange(true)}
                    />
                    Yes
                    <input
                      type="radio"
                      checked={field.value === false}
                      onClick={() => field.onChange(false)}
                      style={{ marginLeft: 20 }}
                    />
                    No
                  </div>
                )}
              />
            </Grid>

            {watch("triedCPAP") === true && (
              <>
                {/* Using CPAP */}
                <Grid item xs={12}>
                  <Controller
                    name="usingCPAP"
                    control={control}
                    render={({ field }) => (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 12,
                        }}
                      >
                        <Typography variant="body2" sx={{ minWidth: 180 }}>
                          Are you currently using CPAP?
                        </Typography>
                        <input
                          type="radio"
                          checked={field.value === true}
                          onClick={() => field.onChange(true)}
                        />
                        Yes
                        <input
                          type="radio"
                          checked={field.value === false}
                          onClick={() => field.onChange(false)}
                          style={{ marginLeft: 20 }}
                        />
                        No
                      </div>
                    )}
                  />
                </Grid>

                {watch("usingCPAP") === true ? (
                  <Grid item xs={12} md={6}>
                    <Controller
                      name="cpapDaysPerWeek"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="How many days a week do you use CPAP?"
                          type="number"
                          fullWidth
                          variant="outlined"
                          size="small"
                          onChange={(e) =>
                            field.onChange(parseInt(e.target.value) || 0)
                          }
                        />
                      )}
                    />
                  </Grid>
                ) : (
                  <Grid item xs={12}>
                    <Typography
                      variant="subtitle2"
                      fontWeight="bold"
                      gutterBottom
                      sx={{ mt: 2 }}
                    >
                      If you are NOT using CPAP, why? (check below)
                    </Typography>

                    <Grid container spacing={2} sx={{ mt: 1 }}>
                      {/* CPAP sinus issues */}
                      <Grid item xs={12} md={6}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            cursor: "pointer",
                          }}
                        >
                          <input
                            type="checkbox"
                            name="cpapCausedSinusIssues"
                            {...register("cpapCausedSinusIssues")}
                            style={{ cursor: "pointer" }}
                            onChange={(e) => {
                              setValue(
                                "cpapCausedSinusIssues",
                                e.target.checked,
                              );
                            }}
                          />
                          <Typography variant="body2">
                            CPAP device irritated nasal passages causing sinus
                            infections
                          </Typography>
                        </div>
                      </Grid>

                      {/* CPAP claustrophobia */}
                      <Grid item xs={12} md={6}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            cursor: "pointer",
                          }}
                        >
                          <input
                            type="checkbox"
                            name="cpapClaustrophobia"
                            {...register("cpapClaustrophobia")}
                            style={{ cursor: "pointer" }}
                            onChange={(e) => {
                              setValue("cpapClaustrophobia", e.target.checked);
                            }}
                          />
                          <Typography variant="body2">
                            Device causing claustrophobia or panic attacks
                          </Typography>
                        </div>
                      </Grid>

                      {/* CPAP GI issues */}
                      <Grid item xs={12} md={6}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            cursor: "pointer",
                          }}
                        >
                          <input
                            type="checkbox"
                            name="cpapgI_Issues"
                            {...register("cpapgI_Issues")}
                            style={{ cursor: "pointer" }}
                            onChange={(e) => {
                              setValue("cpapgI_Issues", e.target.checked);
                            }}
                          />
                          <Typography variant="body2">
                            GI/Stomach/Intestinal issues
                          </Typography>
                        </div>
                      </Grid>

                      {/* CPAP latex allergy */}
                      <Grid item xs={12} md={6}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            cursor: "pointer",
                          }}
                        >
                          <input
                            type="checkbox"
                            name="cpapLatexAllergy"
                            {...register("cpapLatexAllergy")}
                            style={{ cursor: "pointer" }}
                            onChange={(e) => {
                              setValue("cpapLatexAllergy", e.target.checked);
                            }}
                          />
                          <Typography variant="body2">Latex allergy</Typography>
                        </div>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Controller
                          name="cpapOtherReason"
                          control={control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              label="Other (state reason)"
                              fullWidth
                              variant="outlined"
                              size="small"
                            />
                          )}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                )}
              </>
            )}
          </Grid>

          <Grid
            container
            spacing={3}
            sx={{ m: 2, p: 2, backgroundColor: "#f8f9fa", borderRadius: 2 }}
          >
            <Grid item xs={12}>
              <Typography
                variant="h6"
                fontWeight="bold"
                color="primary"
                gutterBottom
              >
                Sleep Study History
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="previousSleepStudy"
                control={control}
                render={({ field }) => (
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 12 }}
                  >
                    <Typography variant="body2" sx={{ minWidth: 180 }}>
                      Previous sleep study
                    </Typography>
                    <input
                      type="radio"
                      checked={field.value === true}
                      onClick={() => field.onChange(true)}
                    />
                    Yes
                    <input
                      type="radio"
                      checked={field.value === false}
                      onClick={() => field.onChange(false)}
                      style={{ marginLeft: 20 }}
                    />
                    No
                  </div>
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Controller
                name="ahI_Score"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="If yes, note AHI score"
                    type="number"
                    fullWidth
                    variant="outlined"
                    size="small"
                    disabled={watch("previousSleepStudy") !== true}
                    onChange={(e) =>
                      field.onChange(parseInt(e.target.value) || 0)
                    }
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography
                variant="caption"
                sx={{ fontStyle: "italic", color: "#666" }}
              >
                (If you do not know & have a copy, please provide to the front
                desk)
              </Typography>
            </Grid>
          </Grid>

          {/* Oral Sleep Appliance */}
          <Grid
            container
            spacing={3}
            sx={{ m: 2, p: 2, backgroundColor: "#f8f9fa", borderRadius: 2 }}
          >
            <Grid item xs={12}>
              <Typography
                variant="h6"
                fontWeight="bold"
                color="primary"
                gutterBottom
              >
                Oral Sleep Appliance
              </Typography>
            </Grid>

            {/* Tried oral sleep appliance */}
            <Grid item xs={12}>
              <Controller
                name="triedOralSleepAppliance"
                control={control}
                render={({ field }) => (
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 12 }}
                  >
                    <Typography variant="body2" sx={{ minWidth: 180 }}>
                      Have you tried an Oral Sleep appliance in the past?
                    </Typography>
                    <input
                      type="radio"
                      checked={field.value === true}
                      onClick={() => field.onChange(true)}
                    />
                    Yes
                    <input
                      type="radio"
                      checked={field.value === false}
                      onClick={() => field.onChange(false)}
                      style={{ marginLeft: 20 }}
                    />
                    No
                  </div>
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Controller
                name="oralApplianceYear"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="If yes, what year?"
                    type="number"
                    fullWidth
                    variant="outlined"
                    size="small"
                    disabled={watch("triedOralSleepAppliance") !== true}
                    onChange={(e) =>
                      field.onChange(parseInt(e.target.value) || 0)
                    }
                  />
                )}
              />
            </Grid>
          </Grid>

          {/* Other Therapies */}
          <Grid
            container
            spacing={3}
            sx={{ m: 2, p: 2, backgroundColor: "#f8f9fa", borderRadius: 2 }}
          >
            <Grid item xs={12}>
              <Typography
                variant="h6"
                fontWeight="bold"
                color="primary"
                gutterBottom
              >
                Other Attempted Therapies
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Please comment about other therapy attempts and how each
                impacted your snoring, apnea, and sleep quality
              </Typography>

              <Controller
                name="otherTherapiesComments"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Comments"
                    fullWidth
                    variant="outlined"
                    size="small"
                    multiline
                    rows={3}
                  />
                )}
              />
            </Grid>
          </Grid>

          {/* Epworth Sleepiness Scale */}
          <Grid
            container
            spacing={3}
            sx={{ m: 2, p: 2, backgroundColor: "#e8f4fd", borderRadius: 2 }}
          >
            <Grid item xs={12}>
              <Typography
                variant="h5"
                align="center"
                fontWeight="bold"
                color="primary"
                gutterBottom
              >
                EPWORTH SLEEPINESS SCALE - TO BE COMPLETED BY THE PATIENT
              </Typography>

              <Typography variant="body2" align="center" sx={{ mb: 3 }}>
                How likely are you to doze off or fall asleep in the following
                situations (in contrast to just feeling tired)? Even if you
                haven't been in some of these situations recently, try to work
                out how they may affect you. Choose the most appropriate number
                for each situation:
              </Typography>

              {/* Scale Legend */}
              <Grid
                container
                spacing={2}
                sx={{ mb: 3, p: 2, backgroundColor: "white", borderRadius: 1 }}
              >
                {[
                  "0 - NEVER doze",
                  "1 - SLIGHT chance",
                  "2 - MODERATE chance",
                  "3 - HIGH chance",
                ].map((label, idx) => (
                  <Grid item xs={12} sm={6} md={3} key={idx}>
                    <Typography
                      variant="body2"
                      fontWeight="bold"
                      color="primary"
                    >
                      {label}
                    </Typography>
                  </Grid>
                ))}
              </Grid>

              {/* Sitting and reading */}
              <Grid container alignItems="center" spacing={2} sx={{ mb: 1 }}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2">Sitting and reading</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Controller
                    name="esS_SittingReading"
                    control={control}
                    render={({ field }) => (
                      <div style={{ display: "flex", gap: 15 }}>
                        <input
                          type="radio"
                          checked={field.value === 0}
                          onClick={() => field.onChange(0)}
                        />{" "}
                        0
                        <input
                          type="radio"
                          checked={field.value === 1}
                          onClick={() => field.onChange(1)}
                        />{" "}
                        1
                        <input
                          type="radio"
                          checked={field.value === 2}
                          onClick={() => field.onChange(2)}
                        />{" "}
                        2
                        <input
                          type="radio"
                          checked={field.value === 3}
                          onClick={() => field.onChange(3)}
                        />{" "}
                        3
                      </div>
                    )}
                  />
                </Grid>
              </Grid>

              {/* Watching TV */}
              <Grid container alignItems="center" spacing={2} sx={{ mb: 1 }}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2">Watching TV</Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Controller
                    name="esS_WatchingTV"
                    control={control}
                    render={({ field }) => (
                      <div style={{ display: "flex", gap: 15 }}>
                        <input
                          type="radio"
                          checked={field.value === 0}
                          onClick={() => field.onChange(0)}
                        />{" "}
                        0
                        <input
                          type="radio"
                          checked={field.value === 1}
                          onClick={() => field.onChange(1)}
                        />{" "}
                        1
                        <input
                          type="radio"
                          checked={field.value === 2}
                          onClick={() => field.onChange(2)}
                        />{" "}
                        2
                        <input
                          type="radio"
                          checked={field.value === 3}
                          onClick={() => field.onChange(3)}
                        />{" "}
                        3
                      </div>
                    )}
                  />
                </Grid>
              </Grid>

              {/* Sitting, inactive in a public place */}
              <Grid container alignItems="center" spacing={2} sx={{ mb: 1 }}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2">
                    Sitting, inactive in a public place (i.e., theatre, meeting)
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Controller
                    name="esS_PublicPlace"
                    control={control}
                    render={({ field }) => (
                      <div style={{ display: "flex", gap: 15 }}>
                        <input
                          type="radio"
                          checked={field.value === 0}
                          onClick={() => field.onChange(0)}
                        />{" "}
                        0
                        <input
                          type="radio"
                          checked={field.value === 1}
                          onClick={() => field.onChange(1)}
                        />{" "}
                        1
                        <input
                          type="radio"
                          checked={field.value === 2}
                          onClick={() => field.onChange(2)}
                        />{" "}
                        2
                        <input
                          type="radio"
                          checked={field.value === 3}
                          onClick={() => field.onChange(3)}
                        />{" "}
                        3
                      </div>
                    )}
                  />
                </Grid>
              </Grid>

              {/* As a passenger in a car */}
              <Grid container alignItems="center" spacing={2} sx={{ mb: 1 }}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2">
                    As a passenger in a car for an hour, without break
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Controller
                    name="esS_PassengerInCar"
                    control={control}
                    render={({ field }) => (
                      <div style={{ display: "flex", gap: 15 }}>
                        <input
                          type="radio"
                          checked={field.value === 0}
                          onClick={() => field.onChange(0)}
                        />{" "}
                        0
                        <input
                          type="radio"
                          checked={field.value === 1}
                          onClick={() => field.onChange(1)}
                        />{" "}
                        1
                        <input
                          type="radio"
                          checked={field.value === 2}
                          onClick={() => field.onChange(2)}
                        />{" "}
                        2
                        <input
                          type="radio"
                          checked={field.value === 3}
                          onClick={() => field.onChange(3)}
                        />{" "}
                        3
                      </div>
                    )}
                  />
                </Grid>
              </Grid>

              {/* Laying to rest */}
              <Grid container alignItems="center" spacing={2} sx={{ mb: 1 }}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2">
                    Laying to rest in the afternoon, when circumstances permit
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Controller
                    name="esS_LayingToRest"
                    control={control}
                    render={({ field }) => (
                      <div style={{ display: "flex", gap: 15 }}>
                        <input
                          type="radio"
                          checked={field.value === 0}
                          onClick={() => field.onChange(0)}
                        />{" "}
                        0
                        <input
                          type="radio"
                          checked={field.value === 1}
                          onClick={() => field.onChange(1)}
                        />{" "}
                        1
                        <input
                          type="radio"
                          checked={field.value === 2}
                          onClick={() => field.onChange(2)}
                        />{" "}
                        2
                        <input
                          type="radio"
                          checked={field.value === 3}
                          onClick={() => field.onChange(3)}
                        />{" "}
                        3
                      </div>
                    )}
                  />
                </Grid>
              </Grid>

              {/* Sitting and talking to someone */}
              <Grid container alignItems="center" spacing={2} sx={{ mb: 1 }}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2">
                    Sitting and talking to someone
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Controller
                    name="esS_TalkingToSomeone"
                    control={control}
                    render={({ field }) => (
                      <div style={{ display: "flex", gap: 15 }}>
                        <input
                          type="radio"
                          checked={field.value === 0}
                          onClick={() => field.onChange(0)}
                        />{" "}
                        0
                        <input
                          type="radio"
                          checked={field.value === 1}
                          onClick={() => field.onChange(1)}
                        />{" "}
                        1
                        <input
                          type="radio"
                          checked={field.value === 2}
                          onClick={() => field.onChange(2)}
                        />{" "}
                        2
                        <input
                          type="radio"
                          checked={field.value === 3}
                          onClick={() => field.onChange(3)}
                        />{" "}
                        3
                      </div>
                    )}
                  />
                </Grid>
              </Grid>

              {/* Sitting quietly after lunch */}
              <Grid container alignItems="center" spacing={2} sx={{ mb: 1 }}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2">
                    Sitting quietly after lunch when NO Alcohol has been
                    consumed
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Controller
                    name="esS_AfterLunchNoAlcohol"
                    control={control}
                    render={({ field }) => (
                      <div style={{ display: "flex", gap: 15 }}>
                        <input
                          type="radio"
                          checked={field.value === 0}
                          onClick={() => field.onChange(0)}
                        />{" "}
                        0
                        <input
                          type="radio"
                          checked={field.value === 1}
                          onClick={() => field.onChange(1)}
                        />{" "}
                        1
                        <input
                          type="radio"
                          checked={field.value === 2}
                          onClick={() => field.onChange(2)}
                        />{" "}
                        2
                        <input
                          type="radio"
                          checked={field.value === 3}
                          onClick={() => field.onChange(3)}
                        />{" "}
                        3
                      </div>
                    )}
                  />
                </Grid>
              </Grid>

              {/* In a car, stationary for a few minutes in traffic */}
              <Grid container alignItems="center" spacing={2} sx={{ mb: 1 }}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2">
                    In a car, stationary for a few minutes in traffic
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Controller
                    name="esS_TrafficStop"
                    control={control}
                    render={({ field }) => (
                      <div style={{ display: "flex", gap: 15 }}>
                        <input
                          type="radio"
                          checked={field.value === 0}
                          onClick={() => field.onChange(0)}
                        />{" "}
                        0
                        <input
                          type="radio"
                          checked={field.value === 1}
                          onClick={() => field.onChange(1)}
                        />{" "}
                        1
                        <input
                          type="radio"
                          checked={field.value === 2}
                          onClick={() => field.onChange(2)}
                        />{" "}
                        2
                        <input
                          type="radio"
                          checked={field.value === 3}
                          onClick={() => field.onChange(3)}
                        />{" "}
                        3
                      </div>
                    )}
                  />
                </Grid>
              </Grid>

              {/* Total Score */}
              <Grid
                container
                spacing={2}
                sx={{
                  mt: 4,
                  p: 2,
                  backgroundColor: "#e8f5e8",
                  borderRadius: 1,
                }}
              >
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" fontWeight="bold">
                    Total Score (0-24):
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    value={totalScore}
                    fullWidth
                    variant="outlined"
                    size="small"
                    InputProps={{ readOnly: true }}
                    sx={{ backgroundColor: "white" }}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          {/* Signature Section */}
          <Grid
            container
            spacing={3}
            sx={{ m: 2, p: 2, backgroundColor: "#f8f9fa", borderRadius: 2 }}
          >
            <Grid item xs={12}>
              <Typography
                variant="h6"
                fontWeight="bold"
                color="primary"
                gutterBottom
              >
                Patient Acknowledgement
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Controller
                name="patientSignature"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Patient Signature"
                    fullWidth
                    variant="outlined"
                    size="small"
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Controller
                name="signatureDate"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Date"
                    type="date"
                    fullWidth
                    variant="outlined"
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    value={datee}
                    onChange={(e) => {
                      field.onChange(e.target.value);
                      setDatee(e.target.value);
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>
        </Grid>
        {/* Submit Button */}
        <Grid
          container
          justifyContent="flex-end"
          alignItems="center"
          spacing={2}
          sx={{ mt: 4 }}
        >
          {/* {FormId > 0 && ( */}
          <Grid item>
            <Button
              variant="contained"
              onClick={handleSubmit((data) => convertToProgressNote(data))}
              disabled={submitting}
              sx={{
                minWidth: 220,
                height: 42,
                backgroundColor: "#4CAF50",
                color: "#fff",
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: 600,
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: "#43A047",
                  boxShadow: "none",
                },
              }}
            >
              Convert to Progress Note
            </Button>
          </Grid>
          {/* // )} */}

          <Grid item>
            <Button
              type="submit"
              variant="contained"
              disabled={submitting}
              sx={{
                minWidth: 120,
                height: 42,
                backgroundColor: "#A574FD",
                color: "#fff",
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: 600,
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: "#8A5CE6",
                  boxShadow: "none",
                },
              }}
            >
              {submitting ? "Saving..." : "Save"}
            </Button>
          </Grid>
        </Grid>
      </form>
    </Container>
  );
};

export default LetterForm;
